import { Refresh } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import classNames from "classnames";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import Markdown from "react-markdown";
import { useParams } from "react-router";
import PDFViewer from "../components/Common/PDFViewer";
import { DashboardLayout } from "../components/DashboardLayout/DashboardLayout";
import { InternalError } from "../components/Errors/InternalError";
import Loading from "../components/Loading";
import AssetDetails from "../components/Orion/AssetDetails";
import ExtractionDisplay from "../components/Orion/ExtractionDisplay";
import {
  useAsset,
  useConfigs,
  useDocument,
} from "../components/Orion/OrionQueries";
import ProcessingDetails from "../components/Orion/ProcessingDetails";
import ValidationOutput from "../components/Orion/ValidationDisplay";
import AssetPageHeader from "../components/PageHeader/AssetPage";
import ChatPdf from "../components/Sirius/ChatPdf";
import ProcessAssetDialog from "../components/useDialogs/ReprocessAssetDialog";
import { useDialogs } from "../components/useDialogs/useDialogs";
import { useSession } from "../components/useSession";
import { PipelineConfigAttrs } from "../interfaces/api";
import { Status } from "../interfaces/common";

export function AssetPage() {
  let params = useParams();
  const assetId = params.assetId as string;
  const [selectedTab, setSelectedTab] = useState(0);
  const dialogs = useDialogs();
  const { session } = useSession();

  // Asset
  const [currentDocId, setCurrentDocId] = useState<string | undefined>(
    undefined
  );
  const [docPages, setDocPages] = useState<number[] | undefined>(undefined);
  const { data: asset, isError, isLoading } = useAsset(assetId);
  const processing = asset?.processing;

  // Configs
  const { data } = useConfigs();
  const configs = useMemo(() => data ?? [], [data]);

  const [config, setConfig] = useState<PipelineConfigAttrs | null>(
    asset?.processing?.config_char ?? configs[0]
  );

  const { data: documentUrl } = useDocument(currentDocId);

  useEffect(() => {
    if (asset?.documents) setCurrentDocId(asset?.documents[0]._id);
    if (processing?.review) setSelectedTab(1);
    if (asset && configs)
      setConfig(asset?.processing?.config_char ?? configs[0]);
  }, [processing, asset, configs]);

  function getContent() {
    if (isError) {
      return <InternalError />;
    } else if (isLoading) {
      return <Loading />;
    } else if (!asset) {
      return <InternalError />;
    } else {
      return (
        <div className="flex flex-1 w-full gap-6 overflow-hidden">
          <PDFViewer
            title={undefined}
            fileUrl={documentUrl?.url}
            pages={docPages}
            className="w-1/2"
          />
          <div
            className={classNames("w-1/2 flex flex-col", {
              "gap-4 overflow-hidden": selectedTab === 0,
              "p-4 overflow-y-auto flex flex-col gap-4": selectedTab === 1,
              "overflow-hidden": selectedTab === 2,
            })}
          >
            {selectedTab === 0 && (
              <>
                <AssetDetails
                  asset={asset}
                  setCurrentDocId={setCurrentDocId}
                  setDocPages={setDocPages}
                />
                <Autocomplete<PipelineConfigAttrs, false, false, false, "div">
                  size="small"
                  onChange={(
                    event: any,
                    config: PipelineConfigAttrs | null
                  ) => {
                    setConfig(config);
                  }}
                  value={config}
                  fullWidth
                  options={configs}
                  className="m-t-4"
                  renderInput={(params) => (
                    <TextField {...params} required label="Configuration" />
                  )}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Box key={key} component="li" {...optionProps}>
                        {option.name}
                      </Box>
                    );
                  }}
                />
                <Button
                  size="small"
                  className="flex gap-2"
                  variant="contained"
                  disabled={
                    !asset ||
                    !config ||
                    ([Status.PENDING, Status.IN_PROGRESS] as any[]).includes(
                      asset.processing?.status
                    )
                  }
                  onClick={async () =>
                    await dialogs.open(ProcessAssetDialog, {
                      asset: asset!,
                      config: config!,
                    })
                  }
                >
                  <Refresh />
                  Relancer une analyse
                </Button>
              </>
            )}
            {selectedTab === 1 && processing?.review && (
              <>
                {processing.review.synthesis && (
                  <Paper className="px-4 pt-2 pb-4">
                    <Typography
                      color="primary"
                      className="text-xl mt-4 font-bold"
                    >
                      Synthèse
                    </Typography>
                    <Markdown>{processing.review.synthesis}</Markdown>
                  </Paper>
                )}
                {processing.review.validation &&
                  _.keys(processing.review.validation.asset_validations)
                    .length > 0 && (
                    <Paper className="px-4 pt-2 pb-4">
                      <ValidationOutput
                        validations={
                          processing.review.validation.asset_validations
                        }
                      />
                    </Paper>
                  )}
                {processing.review.aggregates && (
                  <Paper className="px-4 pt-2 pb-4">
                    <Typography
                      color="primary"
                      className="text-xl mt-4 font-bold"
                    >
                      Données aggrégées
                    </Typography>
                    <Stack className="gap-3">
                      {Object.entries(processing.review.aggregates).map(
                        ([ex_id, result]) => (
                          <ExtractionDisplay key={ex_id} result={result} />
                        )
                      )}
                    </Stack>
                  </Paper>
                )}
                <ProcessingDetails
                  asset={asset}
                  setCurrentDocId={setCurrentDocId}
                  setDocPages={setDocPages}
                />
                <Typography fontSize={10} fontStyle={"italic"}>
                  L'analyse peut comporter des erreurs, pensez à vérifier les
                  informations importantes
                </Typography>
              </>
            )}
            {selectedTab === 2 &&
              processing?._id &&
              session?.OrionChatGroups.includes(asset.workspace_id) && (
                <ChatPdf
                  assetId={asset._id}
                  processing={processing.review?.analysis ?? {}}
                />
              )}
          </div>
        </div>
      );
    }
  }

  return (
    <DashboardLayout>
      <Container
        maxWidth={false}
        className="flex flex-col overflow-hidden h-full"
      >
        <Stack
          spacing={2}
          className="flex flex-col my-2 h-full overflow-hidden"
        >
          <AssetPageHeader
            asset={asset}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />
          {getContent()}
        </Stack>
      </Container>
    </DashboardLayout>
  );
}
